import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { FormNumberInput, FormTextField } from "../../../common/form-fields-wrappers";
import { useRecoilState, useSetRecoilState } from "recoil";
import { keylessAreaState, generalInfoState, keylessInfoState, keylessErrorState } from "../../../search-dms/state";
import { useHasScope } from "../../../../../config/globalConfig";
import { EditRounded } from "@mui/icons-material";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { SET_SNACKBAR } from "../../../common/utils";
import { updateKeylessOperationArea } from "../../../search-dms/dmsSearch.actions";
import { NetworkScope } from "../../../types";
import CustomMap from "./KeylessAreaMap";
import _ from "underscore";
import { CUR_STATUS_DMS} from "../../../data";

const AREA_DEFAULTS = {
  radius: 100,
}

type KeylessOperationAreaProps = {
  isNew: boolean;
  shouldSave: boolean;
  setPayload: (value: any) => void;
  isKeyLess: boolean;
  setPayloadInvalid: (value: boolean) => void;
}

const KeylessOperationArea = (props: KeylessOperationAreaProps) => {
  const { t } = useTranslation(["network", "translation"]);
  const [keylessInfo, setKeylessInfo] = useRecoilState(keylessInfoState);
  const [keylessArea, setKeylessArea] = useRecoilState(keylessAreaState);
  const [generalInfo, setGeneralInfo] = useRecoilState(generalInfoState);
  const [triggerEffect, setTriggerEffect] = useState(false);

  //Circle Values
  const [latitude, setLatitude] = useState(keylessArea?.lat ?? (generalInfo?.latitude != null ? parseFloat(generalInfo.latitude) : ''));
  const [longitude, setLongitude] = useState(keylessArea?.lon ?? (generalInfo?.longitude != null ? parseFloat(generalInfo.longitude) : ''));
  const [radius, setRadius] = useState(keylessArea?.radius ?? AREA_DEFAULTS.radius);

  const keylessOperationAreaForm = useForm({
    mode: "onChange",
    defaultValues: {
      id: keylessArea?.id,
      keyLessInfo: keylessArea?.keyLessInfo ?? "",
      lat: keylessArea?.lat ?? (generalInfo?.latitude != null ? parseFloat(generalInfo.latitude) : ''),
      lon: keylessArea?.lon ?? (generalInfo?.longitude != null ? parseFloat(generalInfo.longitude) : ''),
      radius: keylessArea?.radius ?? AREA_DEFAULTS.radius,
      mobilityStoreId: keylessArea?.mobilityStoreId
        ? keylessArea?.mobilityStoreId
        : generalInfo.id,
    },
  });

  const { errors: formErrors } = useFormState(keylessOperationAreaForm);
  const formHasErrors = !_.isEmpty(formErrors);

  const hasScope = useHasScope;
  const theme = useTheme();
  const { dispatchGlobalData } = useContext(GlobalContext);
  const [isDisabled, setIsDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDisableForm = (value: boolean) => {
    setIsDisabled(value);
  };

  function isNumberWithOptionalDecimal(value: string) {
    const regex = /^-?\d+(\.\d+)?(\.)?$/;
    return regex.test(value);
  }

  function isNumber(value: string) {
    const regex = /^\d+$/;
    return regex.test(value);
  }

  function checkLongitudeMax(value: number) {
    return value >= -180 && value <= 180;
  }

  function checkLatitudeMax(value: number) {
    return value >= -90 && value <= 90;
  }

  //Check if the values for the circle is valid, and reset the value if not
  const handleChangeCircleValues = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | Number, fieldName: string) => {
    //console.log("handleChangeLat --> fieldName: " + fieldName);
    const valueStr = (() => {
      if (typeof e === "number") return e.toString();
      return (e as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
    })();
    //console.log("handleChangeLat --> valueStr: " + valueStr);

    let newValue;
    let checkValue = false;
    let isRadius = false;

    //console.log("handleChangeLat --> fieldName: " + fieldName);
    //Check on the input: if number or decimal
    if (fieldName === "radius") {
      isRadius = true;
      checkValue = isNumber(valueStr);
    } else {
      //lat, lon
      checkValue = isNumberWithOptionalDecimal(valueStr);
    }

    //console.log("handleChangeLat --> checkValue: " + checkValue);
    if (checkValue) {
      if (isRadius) {
        newValue = parseInt(valueStr);
        setRadius(newValue);
        return;
      } else {
        newValue = parseFloat(valueStr);
        if (fieldName === "lat") {
          if (checkLatitudeMax(newValue)) {
            setLatitude(newValue);
            return;
          }
        } else {
          //Lon
          if (checkLongitudeMax(newValue)) {
            setLongitude(newValue);
            return;
          }
        }
      }
    }

    //If the value is not valid: remove the focus and start the useEffect to reset the value
    console.log("handleChangeLat --> Not Valid Number!");
    if (Object.prototype.hasOwnProperty.call(e, "target")) {
      (e as any).target.blur(); // Remove focus
    }
    setTriggerEffect(true); //Used to force the call of the useEffect

    //TODO some kind of error message to the user??
  };

  const [fieldsWithErrors, setFieldsWithErrors] = useState({} as { [k: string]: boolean });
  function convertFormData(data: any) {
    const defaultValues = keylessOperationAreaForm.formState.defaultValues!;

    const fieldIsEmpty = (name: keyof typeof defaultValues): boolean => {
      return (data[name] === undefined)
          || (data[name] === null)
          || (data[name] === '');
    }

    const isFloat = (v: any) => /^-?\d+(\.\d+)?$/.test(v?.toString());
    const isPositiveInt = (v: any) => /^\d+$/.test(v?.toString());

    const fieldsWithErrorsList = Object.keys(defaultValues).filter(fieldIsEmpty as (v: string) => boolean);

    if (! isFloat(data.lat)) {
      fieldsWithErrorsList.push("lat");
    }

    if (! isFloat(data.lon)) {
      fieldsWithErrorsList.push("lon");
    }

    if (! isPositiveInt(data.radius)) {
      fieldsWithErrorsList.push("radius");
    }

    if (fieldsWithErrorsList.includes("id")) {
      const idIdx = fieldsWithErrorsList.findIndex(v => v === "id");
      fieldsWithErrorsList.splice(idIdx, 1);
  }

    setFieldsWithErrors(Object.fromEntries(fieldsWithErrorsList.map((field: string) => [field, true])));

    if (props.isKeyLess && (formHasErrors || fieldsWithErrorsList.length)) {
      console.log({ fieldsWithErrorsList, formHasErrors })
      if (props.shouldSave) {
        props.setPayloadInvalid(true);
      }
      return null;
    }

    return {
      id: keylessArea?.id,
      keyLessInfo: data?.keyLessInfo,
      lat: data?.lat,
      lon: data?.lon,
      radius: data?.radius,
      mobilityStoreId: keylessArea?.mobilityStoreId
        ? keylessArea?.mobilityStoreId
        : generalInfo.id,
    };
  }

  useEffect(() => {
    if (props.isNew) {
      handleDisableForm(false);
    } else {
      handleDisableForm(true);
    }
  }, [props.isNew]);

  useEffect(() => {
    if (props.shouldSave) {
      const data = convertFormData(keylessOperationAreaForm.getValues());
      if (!data) return; // Error was thrown
      props.setPayload(data);
    }
  }, [props.shouldSave]);

  const setKeylessError = useSetRecoilState(keylessErrorState);
  const onSubmit = (data: any) => {
    if (formHasErrors) {
      if (props.shouldSave) {
        props.setPayloadInvalid(true);
      }
      dispatchGlobalData(SET_SNACKBAR.ERROR);
      return;
    }

    const reqParameters = convertFormData(data);
    if (!reqParameters) return; // Error was thrown
    setOpen(false);
    if (!reqParameters) return; // Error was thrown
    console.log(
      "updateKeylessOperationArea --> res :" + JSON.stringify(reqParameters)
    );

    updateKeylessOperationArea(reqParameters)
      .then((res: any) => {
        console.log(
          "updateKeylessOperationArea --> res :" + JSON.stringify(res)
        );
        setKeylessArea(res?.msKeylessArea);
        setIsDisabled(true);
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
      })
      .catch((err: any) => {
        setKeylessError(err);
      });
  };

  useEffect(() => {
    //console.log("useEffect --> keylessArea: " + JSON.stringify(keylessArea));
    setTriggerEffect(false); //Used to force the call of the useEffect

    keylessOperationAreaForm.resetField("lat", { defaultValue: latitude });
    keylessOperationAreaForm.resetField("lon", { defaultValue: longitude });
    keylessOperationAreaForm.resetField("radius", { defaultValue: radius });
  }, [
    triggerEffect,
    latitude,
    longitude,
    radius,
    keylessArea,
    keylessOperationAreaForm,
  ]);

  const fieldIsDisabled = (name: string): boolean => {
    if (props.isNew) {
      return !props.isKeyLess
    }

    if (!props.isNew) {
      return isDisabled || !props.isKeyLess;
    }

    return true; // Unreachable statement
  }

  const onMapClick = (e: google.maps.MapMouseEvent) => {
    if (fieldIsDisabled("map")) return;

    console.log("map mouse", e.latLng?.lat(), e.latLng?.lng())
    setLatitude(e.latLng?.lat())
    setLongitude(e.latLng?.lng())
  }

  return (
    <>
      <FormProvider {...keylessOperationAreaForm}>
        <Grid container item direction="row" xs={12} columnSpacing={2}>
          <Grid item xs={12}>
            <Stack
              direction="column"
              spacing={2}
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              {hasScope(NetworkScope.MsMng) && isDisabled && generalInfo?.currentStatus !==  CUR_STATUS_DMS.TERMINATED &&(
                <IconButton
                  color={"accent"}
                  onClick={() => {
                    handleDisableForm(false);
                  }}
                >
                  <EditRounded />
                </IconButton>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <FormTextField
              error={!!fieldsWithErrors.keyLessInfo}
              disabled={fieldIsDisabled("keyLessInfo")}
              control={keylessOperationAreaForm.control}
              name="keyLessInfo"
              label={t("dmsDetail.keyless.operationAreaTab.keyLessInfo")}
              multiline
              rows={3}
              fullWidth
              InputProps={{
                style: {
                  border: "2px dashed #ccc",
                  borderRadius: "5px",
                },
              }}
            />
          </Grid>

          <Grid item xs={3}>
            <FormTextField
              error={!!fieldsWithErrors.lat}
              disabled={fieldIsDisabled("lat")}
              control={keylessOperationAreaForm.control}
              name="lat"
              label={t("dmsDetail.keyless.operationAreaTab.latitude")}
              onChange={(e) => handleChangeCircleValues(e, "lat")}
            />
          </Grid>
          <Grid item xs={3}>
            <FormTextField
              error={!!fieldsWithErrors.lon}
              disabled={fieldIsDisabled("lon")}
              control={keylessOperationAreaForm.control}
              name="lon"
              label={t("dmsDetail.keyless.operationAreaTab.longitude")}
              onChange={(e) => handleChangeCircleValues(e, "lon")}
            />
          </Grid>
          <Grid item xs={3}>
            <Tooltip title={t('dmsDetail.keyless.operationAreaTab.tooltips.radius')}>
              <FormNumberInput
                error={!!fieldsWithErrors.radius}
                disabled={fieldIsDisabled("radius")}
                control={keylessOperationAreaForm.control}
                name="radius"
                label={t("dmsDetail.keyless.operationAreaTab.radius")}
                onChange={(e: number) => handleChangeCircleValues(e, "radius")}
              />
            </Tooltip>
          </Grid>
        </Grid>

        <CustomMap latitude={latitude || 0} longitude={longitude || 0} radius={radius} onClick={onMapClick} />

        <Grid item xs={12} sx={{ marginTop: theme.spacing(2) }}>
          <Stack
            direction="column"
            spacing={2}
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            {!isDisabled && !props.isNew && (
              <Button variant="contained" onClick={handleOpen} disabled={formHasErrors} >
                {/* todo: update translations */}
                {t("dmsDetail.keyless.operationAreaTab.button.edit")}
              </Button>
            )}
          </Stack>
        </Grid>
      </FormProvider>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {/* todo: update translations */}
          {t("dmsDetail.keyless.operationAreaTab.dialog.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {/* todo: update translations */}
            {t("dmsDetail.keyless.operationAreaTab.dialog.content")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant={"outlined"} color={"accent"}>
            {/* todo: update translations */}
            {t("dmsDetail.keyless.operationAreaTab.dialog.action.cancel")}
          </Button>
          <Button
            onClick={keylessOperationAreaForm.handleSubmit(onSubmit)}
            autoFocus
          >
            {/* todo: update translations */}
            {t("dmsDetail.keyless.operationAreaTab.dialog.action.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KeylessOperationArea;
